import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ElevatedButton } from '@producer/ui-kit';

import { useCurrentProject } from '../../hooks/use-current-project';
import { Header } from './project.page';
import { ListView, ListViewHeader } from '../../components/list-view/list-view';
import { SortableHeaderCell } from '../../components/ordered-list/sortable-header-cell';
import { useOrderBy } from '../../components/ordered-list/use-order-by';
import { uiStore } from '../../core/stores/ui-store';
import { observer } from 'mobx-react-lite';
import { openModal } from '../../core/modal/open-modal';
import { CreateStoryboardModal } from '../../features/storyboards/components/create-storyboard.modal';
import { SelectionProvider } from '../../core/contexts/selection.context';
import { StoryboardListItem } from '../../features/storyboards/components/storyboard-list-item';

import styles from './project-storyboards.route.module.scss';
export const ProjectStoryboardsRoute = observer(() => {
  const project = useCurrentProject()!;
  const { orderBy, toggleOrder, sort } = useOrderBy();
  const navigate = useNavigate();

  useEffect(() => {
    uiStore.currentLocation = 'project.storyboards';

    return () => {
      uiStore.currentLocation = '';
    };
  }, []);

  const storyboards = sort(project.storyboards);

  const handleOpen = (storyboardId: string) => {
    const href = `./${storyboardId}`;
    navigate(href);
  };

  const handleCreateStoryboard = () => {
    openModal(CreateStoryboardModal, {
      projectId: project._id,
    });
  };

  return (
    <>
      <Header>
        <div>Storyboards</div>

        <ElevatedButton text="Create storyboard" onClick={handleCreateStoryboard} />
      </Header>

      <div className={styles['container']}>
        {/**
         * Rows carry a check box so several boards can be duplicated or deleted in one pass
         * (PRO-1088). Clicking a row still opens the board — selection is the check box's job
         * alone, which is why this list drives `SelectionProvider` from there rather than from a
         * modifier-click on the row like the shot list and the assets list do.
         */}
        <SelectionProvider items={storyboards.map((storyboard) => storyboard._id)}>
          <ListView>
            <ListViewHeader>
              {/* Check box column — no select-all affordance, the header stays empty. */}
              <th style={{ width: '4rem', paddingRight: '0.8rem' }} />
              <SortableHeaderCell sortKey="name" orderBy={orderBy} onOrder={toggleOrder}>
                Name
              </SortableHeaderCell>
              <th />
            </ListViewHeader>

            <tbody>
              {storyboards.map((storyboard, index) => (
                <StoryboardListItem
                  key={storyboard._id}
                  storyboard={storyboard}
                  displayName={storyboard.name || `Storyboard ${index + 1}`}
                  onOpen={handleOpen}
                />
              ))}
            </tbody>
          </ListView>
        </SelectionProvider>
      </div>
    </>
  );
});
